import type { CatalogEvent } from "@/features/catalog/mediators/catalogEvents";
import { createMediator, type Mediator } from "@/shared/lib/componentHierarchy";

export type CatalogTagEvent = Extract<
  CatalogEvent,
  {
    type:
      | "catalog/tags-synchronized"
      | "catalog/tag-selection-toggled"
      | "catalog/tag-selection-cleared"
      | "catalog/tag-visibility-toggled"
      | "catalog/hidden-tags-cleared";
  }
>;

export type CatalogTagState = {
  readonly selectedTag: string | null;
  readonly hiddenTags: ReadonlySet<string>;
};

export type CatalogTagEffect =
  | { readonly type: "select"; readonly tag: string | null }
  | { readonly type: "hide"; readonly tag: string }
  | { readonly type: "show"; readonly tag: string }
  | { readonly type: "clear-hidden" };

export function isCatalogTagEvent(
  event: CatalogEvent,
): event is CatalogTagEvent {
  return (
    event.type === "catalog/tags-synchronized" ||
    event.type === "catalog/tag-selection-toggled" ||
    event.type === "catalog/tag-selection-cleared" ||
    event.type === "catalog/tag-visibility-toggled" ||
    event.type === "catalog/hidden-tags-cleared"
  );
}

export function createCatalogTagMediator(
  initial: {
    readonly selectedTag: string | null;
    readonly hiddenTags: readonly string[];
  },
  runEffect: (effect: CatalogTagEffect) => void,
): Mediator<CatalogTagState, CatalogTagEvent, CatalogTagEffect> {
  return createMediator<CatalogTagState, CatalogTagEvent, CatalogTagEffect>(
    toState(initial),
    (state, event) => {
      switch (event.type) {
        case "catalog/tags-synchronized":
          return { status: "handled", state: toState(event) };
        case "catalog/tag-selection-toggled": {
          const tag = state.selectedTag === event.tag ? null : event.tag;
          return {
            status: "handled",
            state: { ...state, selectedTag: tag },
            effects: [{ type: "select", tag }],
          };
        }
        case "catalog/tag-selection-cleared":
          return {
            status: "handled",
            state: { ...state, selectedTag: null },
            effects: [{ type: "select", tag: null }],
          };
        case "catalog/tag-visibility-toggled": {
          const hidden = state.hiddenTags.has(event.tag);
          const hiddenTags = new Set(state.hiddenTags);
          if (hidden) hiddenTags.delete(event.tag);
          else hiddenTags.add(event.tag);
          return {
            status: "handled",
            state: {
              ...state,
              hiddenTags,
              selectedTag:
                !hidden && state.selectedTag === event.tag
                  ? null
                  : state.selectedTag,
            },
            effects: [{ type: hidden ? "show" : "hide", tag: event.tag }],
          };
        }
        case "catalog/hidden-tags-cleared":
          return {
            status: "handled",
            state: { ...state, hiddenTags: new Set() },
            effects: [{ type: "clear-hidden" }],
          };
      }
    },
    { runEffect },
  );
}

function toState(model: {
  readonly selectedTag: string | null;
  readonly hiddenTags: readonly string[];
}): CatalogTagState {
  return {
    selectedTag: model.selectedTag,
    hiddenTags: new Set(model.hiddenTags),
  };
}
